/**
 * Offer form (CTR)
 * Posts sign-ups from the theme's own form to Aisle's "Subscribe User" endpoint.
 * Docs: https://documentation.gotoaisle.com/api-endpoints/subscribe
 *
 * The host element carries the config as data attributes:
 *   data-endpoint, data-api-key, data-campaign-phone
 * and a <script type="application/json" data-offer-form-messages> with the
 * user-facing messages keyed by Aisle response code.
 */

// Aisle's recommended validation pattern (US numbers, optional country code).
const PHONE_PATTERN = /^(\+\d{1,2})?[\s.-]?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const KNOWN_CODES = [
  'ConvoAlreadyExists',
  'ConvoStopped',
  'EmailAlreadyExists',
  'UserNotEligible',
  'VoipCustomer',
  'PayoutBlocked',
];
const UTM_MAP = {
  utm_campaign: 'utmCampaign',
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
};

class OfferFormComponent extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form || this.form.dataset.bound === 'true') return;
    this.form.dataset.bound = 'true';

    this.phone = this.form.querySelector('[name="phone"]');
    this.email = this.form.querySelector('[name="email"]');
    this.consent = this.form.querySelector('[name="consent"]');
    this.submit = this.form.querySelector('[type="submit"]');
    this.message = this.form.querySelector('[data-offer-form-message]');
    this.success = this.querySelector('[data-offer-form-success]');

    try {
      this.messages = JSON.parse(this.querySelector('[data-offer-form-messages]')?.textContent || '{}');
    } catch (error) {
      this.messages = {};
    }

    this.form.addEventListener('submit', (event) => this.handleSubmit(event));
    [this.phone, this.email, this.consent].forEach((field) => {
      field?.addEventListener('input', () => this.clearMessage());
    });
  }

  text(key) {
    return this.messages[key] || this.messages.error || 'Something went wrong. Please try again.';
  }

  showMessage(text, tone = 'error') {
    if (!this.message) return;
    this.message.textContent = text;
    this.message.dataset.tone = tone;
    this.message.hidden = false;
  }

  clearMessage() {
    if (!this.message) return;
    this.message.hidden = true;
    this.message.textContent = '';
  }

  // Send E.164 for US numbers so Aisle does not have to guess the format.
  normalizePhone(raw) {
    const digits = raw.replace(/\D/g, '');
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    return raw.trim();
  }

  validate() {
    const phone = this.phone?.value.trim() || '';
    if (!PHONE_PATTERN.test(phone)) {
      this.showMessage(this.text('invalid_phone'));
      this.phone?.focus();
      return null;
    }

    const email = this.email?.value.trim() || '';
    if (this.email && ((this.email.required && !email) || (email && !EMAIL_PATTERN.test(email)))) {
      this.showMessage(this.text('invalid_email'));
      this.email.focus();
      return null;
    }

    if (this.consent && !this.consent.checked) {
      this.showMessage(this.text('consent'));
      this.consent.focus();
      return null;
    }

    return { phone, email };
  }

  buildPayload({ phone, email }) {
    const params = new URLSearchParams(window.location.search);
    const payload = {
      customerPhoneNumber: this.normalizePhone(phone),
      campaignPhoneNumber: this.dataset.campaignPhone || '',
      referrerUrl: window.location.href,
    };
    if (email) payload.email = email;

    Object.entries(UTM_MAP).forEach(([param, key]) => {
      const value = params.get(param);
      if (value) payload[key] = value;
    });

    const influencerTag = params.get('code') || params.get('influencer') || params.get('ref');
    if (influencerTag) payload.influencerTag = influencerTag;

    return payload;
  }

  async handleSubmit(event) {
    event.preventDefault();
    if (this.busy) return;

    const values = this.validate();
    if (!values) return;

    if (!this.dataset.apiKey || !this.dataset.campaignPhone) {
      this.showMessage(this.text('error'));
      return;
    }

    this.clearMessage();
    this.setBusy(true);

    try {
      const response = await fetch(this.dataset.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Api-Key': this.dataset.apiKey,
        },
        body: JSON.stringify(this.buildPayload(values)),
      });

      if (response.status === 429) {
        this.showMessage(this.text('rate_limited'));
        return;
      }

      let data = {};
      try {
        data = await response.json();
      } catch (error) {
        data = {};
      }

      const code = data.code || (response.ok ? 'NewConvoCreated' : 'error');

      if (code === 'NewConvoCreated') {
        this.showSuccess();
        return;
      }

      this.showMessage(
        this.text(KNOWN_CODES.includes(code) ? code : 'error'),
        code === 'ConvoAlreadyExists' ? 'info' : 'error'
      );
    } catch (error) {
      this.showMessage(this.text('error'));
    } finally {
      this.setBusy(false);
    }
  }

  showSuccess() {
    this.form.hidden = true;
    if (this.success) {
      this.success.hidden = false;
      this.success.setAttribute('tabindex', '-1');
      this.success.focus();
    }
    this.dispatchEvent(new CustomEvent('offer-form:success', { bubbles: true }));
  }

  setBusy(state) {
    this.busy = state;
    if (this.submit) {
      this.submit.disabled = state;
      this.submit.setAttribute('aria-busy', String(state));
    }
    this.classList.toggle('is-busy', state);
  }
}

if (!customElements.get('offer-form-component')) {
  customElements.define('offer-form-component', OfferFormComponent);
}
